import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, BRAND, formatApiErrorDetail } from "@/lib/avocado";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

export function ForgotPasswordDialog({ open, onOpenChange }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setSent(false); setEmail(""); } onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="forgot-dialog">
        <DialogHeader>
          <p className="uppercase-label" style={{ color: BRAND.pink }}>Password reset</p>
          <DialogTitle className="font-display text-3xl">Forgot email or password?</DialogTitle>
          <DialogDescription>Pop your email in below and we'll send you a reset link. Link expires in an hour.</DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="rounded-xl p-5 mt-2 flex items-start gap-3" style={{ background: "#FFF0F8", border: "2px solid #FFAED8" }}>
            <CheckCircle2 size={20} style={{ color: BRAND.pink, marginTop: 2 }} />
            <div className="text-sm" style={{ color: "#171717" }}>
              If <strong>{email}</strong> is on file, a reset link is on its way. Check your inbox (and spam folder, just in case).
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="uppercase-label">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-11 rounded-xl" data-testid="forgot-email-input" />
            </div>
            <button type="submit" disabled={busy} className="btn-brand w-full h-11 rounded-full font-bold flex items-center justify-center" data-testid="forgot-submit">
              {busy ? <Loader2 className="animate-spin" size={16} /> : "Send reset link"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { if (!token) navigate("/login"); }, [token, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset — sign in with your new password");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#FFFCF7" }}>
      <div className="w-full max-w-md">
        <div className="mb-8"><BrandLogo /></div>
        <p className="uppercase-label" style={{ color: BRAND.pink }}>Choose a new password</p>
        <h1 className="font-display text-4xl md:text-5xl mt-1 mb-2" style={{ fontWeight: 700 }}>Reset <span className="italic">password.</span></h1>
        <p className="text-sm mb-8" style={{ color: "#7A6D5F" }}>Pick something at least 6 characters. You'll be signed in on the next screen.</p>
        {done ? (
          <div className="rounded-xl p-5 flex items-start gap-3" style={{ background: "#FFF0F8", border: "2px solid #FFAED8" }}>
            <CheckCircle2 size={22} style={{ color: BRAND.pink, marginTop: 2 }} />
            <div className="text-sm">All done! Redirecting you to sign in…</div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
            <div className="space-y-1.5">
              <Label className="uppercase-label">New password</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="h-11 rounded-xl" data-testid="reset-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="uppercase-label">Confirm password</Label>
              <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="h-11 rounded-xl" data-testid="reset-confirm-input" />
            </div>
            <button type="submit" disabled={busy} className="btn-brand w-full h-12 rounded-full font-bold flex items-center justify-center" data-testid="reset-submit">
              {busy ? <Loader2 className="animate-spin" size={18} /> : "Set new password"}
            </button>
          </form>
        )}
        <p className="text-sm mt-6" style={{ color: "#7A6D5F" }}>
          <Link to="/login" className="font-bold underline underline-offset-4" style={{ color: BRAND.pink }}>← Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
